/**
 * SandboxCanvasHeader — compact, flushed single-row header over the sandbox
 * canvas, mirroring the Flow Designer / Exploratory-Test-Detail header.
 *
 * Left:  template name · instance status pill · purpose badge · repo@branch ·
 *        live expiry countdown (or held-for-debug).
 * Right: Open URL (live + public) · Logs icon (opens the slide-up overlay) ·
 *        overflow menu (terminate / delete / view template / copy IDs /
 *        connect locally).
 */
import React from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  TagBadge,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@chatsdet/ui';
import {
  ArrowSquareOut,
  DotsThreeVertical,
  Gear,
  GitBranch,
  Lock,
  Terminal,
} from '@phosphor-icons/react';
import type { EnvironmentInstance } from '../../../hooks/useEnvironmentInstances';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';
import { BRAND } from '../../../brand';
import { PurposeBadge } from './PurposeBadge';
import { InstanceStatusBadge } from './InstanceStatusBadge';
import { SandboxExpiryCountdown } from './SandboxExpiryCountdown';

export interface SandboxCanvasHeaderProps {
  template: EnvironmentTemplate;
  activeInstance: EnvironmentInstance;
  onOpenLogs: () => void;
  onOpenSettings: () => void;
  onConnectLocally: () => void;
  onTerminate: () => void;
  onDelete: () => void;
  onViewTemplate: () => void;
  onCopyTemplateId: () => void;
  onCopyInstanceId: () => void;
}

export function SandboxCanvasHeader({
  template,
  activeInstance,
  onOpenLogs,
  onOpenSettings,
  onConnectLocally,
  onTerminate,
  onDelete,
  onViewTemplate,
  onCopyTemplateId,
  onCopyInstanceId,
}: SandboxCanvasHeaderProps) {
  const { cliCommand } = BRAND;

  // "Live" = the instance has reachable resources. Open URL, the Private pill,
  // Connect locally, termination, and the expiry countdown all only make sense
  // in this set; for stopping/failed/terminated they're misleading.
  const isLive = ['pending', 'provisioning', 'deploying', 'running'].includes(
    activeInstance.status
  );

  return (
    <div className="flex items-center justify-between gap-3 px-4 h-14 shrink-0 border-b border-border bg-background">
      <div className="flex items-baseline gap-3 min-w-0 flex-1">
        <Text weight="semibold" className="text-base truncate shrink-0">
          {template.name}
        </Text>
        <InstanceStatusBadge status={activeInstance.status} size="sm" />
        <PurposeBadge purpose={activeInstance.purpose} size="sm" className="shrink-0 self-center" />
        <span className="hidden md:inline-flex items-center gap-1 text-xs text-muted-foreground min-w-0 self-center">
          {template.repositoryFullName ? (
            <>
              <GitBranch size={12} className="shrink-0" />
              <span className="font-mono truncate">
                {template.repositoryFullName}
                <span className="text-slate-300">@</span>
                {template.branch}
              </span>
            </>
          ) : (
            <span className="truncate">No repository (empty workspace)</span>
          )}
        </span>
        {activeInstance.preservedForDebugUntil ? (
          <TooltipTrigger delay={200}>
            <span className="shrink-0 self-center">
              <TagBadge name="Held for debug" color="orange" size="sm" hideDot />
            </span>
            <Tooltip>EC2 kept alive for SSM debugging until it&apos;s reaped.</Tooltip>
          </TooltipTrigger>
        ) : isLive ? (
          <SandboxExpiryCountdown
            expiresAt={activeInstance.expiresAt}
            variant="compact"
            className="hidden lg:inline-flex shrink-0 self-center text-xs text-muted-foreground"
          />
        ) : null}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isLive && activeInstance.publicUrl && !activeInstance.isPrivate ? (
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Open URL"
            aria-label="Open sandbox URL"
            iconLeft={<ArrowSquareOut size={16} />}
            onPress={() =>
              window.open(activeInstance.publicUrl!, '_blank', 'noopener,noreferrer')
            }
          />
        ) : isLive && activeInstance.isPrivate ? (
          <TooltipTrigger delay={200}>
            <span className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border bg-slate-50 text-slate-600 text-xs">
              <Lock size={12} /> Private
            </span>
            <Tooltip>No public URL — connect via {cliCommand}-cli</Tooltip>
          </TooltipTrigger>
        ) : null}
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Logs"
          aria-label="View logs"
          iconLeft={<Terminal size={16} />}
          onPress={onOpenLogs}
        />
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Sandbox details"
          aria-label="Sandbox details"
          iconLeft={<Gear size={16} />}
          onPress={onOpenSettings}
        />
        <MenuTrigger>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="More actions"
            aria-label="More actions"
          >
            <DotsThreeVertical size={18} />
          </Button>
          <Menu className="w-56">
            <MenuItem onAction={onConnectLocally}>Connect locally</MenuItem>
            <MenuSeparator />
            {isLive ? (
              <MenuItem destructive onAction={onTerminate}>
                Terminate sandbox
              </MenuItem>
            ) : null}
            <MenuItem destructive onAction={onDelete}>
              Delete sandbox
            </MenuItem>
            <MenuSeparator />
            <MenuItem onAction={onViewTemplate}>View template</MenuItem>
            <MenuItem onAction={onCopyTemplateId}>Copy template ID</MenuItem>
            <MenuItem onAction={onCopyInstanceId}>Copy instance ID</MenuItem>
          </Menu>
        </MenuTrigger>
      </div>
    </div>
  );
}

SandboxCanvasHeader.displayName = 'SandboxCanvasHeader';
